import React, { useMemo, useCallback } from 'react';
import { ReactComponent as FilterOn } from 'assets/icons/ui-elements/small/filter-on-sm.svg';
import { ReactComponent as FilterOff } from 'assets/icons/ui-elements/small/filter-off-sm.svg';
import { ReactComponent as XIcon } from 'assets/icons/ui-elements/small/x-mark-sm.svg';
import { LkpLoading } from 'components';
import type { UseAgendaMobileReturn } from '../../../hooks/useAgendaMobile';
import { EventColorMapper, EventColorSet } from '../../../utils/eventColors';
import { MobileWeekCalendar } from './MobileWeekCalendar';
import { MobileDayCardList } from './MobileDayCardList';
import { AgendaMobileDayEmptyState } from './AgendaMobileDayEmptyState';
import { CalendarFilters } from './CalendarFilters';
import { MultiSelectDropdown } from 'newComponents/Form/Multiselect';
import { ReactComponent as EyeOn } from 'assets/icons/ui-elements/small/eye-on-sm.svg';
import { DisplayMode } from '../../../hooks/useCalendarFilters';
import {
  MobileToolbarRow,
  MobileFiltersExpandable,
  MobileDayListSection,
  MobileLoadingOverlay,
  TypeFilterContainer,
  TypeButton,
  FiltersButtonContainer,
  FiltersButton,
  UnidadesFieldWrapper,
} from '../styled';

const DISPLAY_MODE_OPTIONS = [
  {
    label: 'Exibir na agenda:',
    items: [
      { label: 'Atividades', value: 'atividades' },
      { label: 'Ocorrências', value: 'ocorrencias' },
    ],
  },
];

function displayModeToValues(mode: DisplayMode): string[] {
  if (mode === 'ambos') return ['atividades', 'ocorrencias'];
  return [mode];
}

function valuesToDisplayMode(values: string[]): DisplayMode {
  const hasA = values.includes('atividades');
  const hasO = values.includes('ocorrencias');
  if (hasA && !hasO) return 'atividades';
  if (hasO && !hasA) return 'ocorrencias';
  return 'ambos';
}

interface AgendaMobileLayoutProps extends UseAgendaMobileReturn {
  onEventClick: (event: import('pages/Agenda/utils/eventUtils').CalendarEvent, colorSet: EventColorSet) => void;
  canViewOccurrences: boolean;
}

export const AgendaMobileLayout: React.FC<AgendaMobileLayoutProps> = props => {
  const {
    currentDate,
    weekDays,
    navigate,
    selectedDate,
    setSelectedDate,
    events,
    eventsForSelectedDay,
    horasPrevistasSelectedDay,
    type,
    setType,
    openFilters,
    setOpenFilters,
    hasActiveFilters,
    clearAllFilters,
    planosSelectionLabel,
    executoresSetoresSelectionLabel,
    ambientesEquipamentosSelectionLabel,
    maintenanceUnitsOptions,
    maintenanceUnitsValues,
    handleMaintenanceUnitsChange,
    setExecutoresSetoresModalOpen,
    setAmbientesEquipamentosModalOpen,
    setPlanosModalOpen,
    isLoadingEvents,
    onEventClick,
    displayMode,
    setDisplayMode,
    canViewOccurrences,
  } = props;

  const colorMapper = useMemo(() => new EventColorMapper(), []);
  const getColorSet = useCallback(
    (event: { title: string }) => colorMapper.getColorForEvent(event.title),
    [colorMapper],
  );

  return (
    <div data-testid="agenda-mobile-layout" style={{ position: 'relative' }}>
      {isLoadingEvents && (
        <MobileLoadingOverlay>
          <LkpLoading />
        </MobileLoadingOverlay>
      )}
      <MobileWeekCalendar
        currentDate={currentDate}
        weekDays={weekDays}
        selectedDate={selectedDate}
        onSelectDay={setSelectedDate}
        onNavigate={navigate}
        eventsByDate={events}
      />
      <MobileToolbarRow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <TypeFilterContainer>
            <TypeButton isActive={type === 'pendentes'} onClick={() => setType('pendentes')} isRight={false}>
              Pendentes
            </TypeButton>
            <TypeButton isActive={type === 'baixadas'} onClick={() => setType('baixadas')} isRight>
              Baixadas
            </TypeButton>
          </TypeFilterContainer>
          {canViewOccurrences && (
            <UnidadesFieldWrapper>
              <MultiSelectDropdown
                options={DISPLAY_MODE_OPTIONS}
                value={displayModeToValues(displayMode)}
                onChange={values => setDisplayMode(valuesToDisplayMode(values))}
                size="sm"
                width="100%"
                iconLeft={[<EyeOn key="eye-on-icon" />]}
                temCategorias
                allSelectedLabel="Atividades e ocorrências"
              />
            </UnidadesFieldWrapper>
          )}
        </div>
        <FiltersButtonContainer>
          <FiltersButton type="button" onClick={() => setOpenFilters(!openFilters)} aria-expanded={openFilters}>
            {openFilters ? <FilterOff /> : <FilterOn />}
            Filtros
          </FiltersButton>
          {hasActiveFilters && (
            <FiltersButton onClick={clearAllFilters}>
              <XIcon />
              Limpar Filtros
            </FiltersButton>
          )}
        </FiltersButtonContainer>
      </MobileToolbarRow>
      {openFilters && (
        <MobileFiltersExpandable>
          <CalendarFilters
            type={type}
            onTypeChange={setType}
            onOpenExecutoresModal={() => setExecutoresSetoresModalOpen(true)}
            onOpenAmbientesModal={() => setAmbientesEquipamentosModalOpen(true)}
            onOpenPlanosModal={() => setPlanosModalOpen(true)}
            planosSelectionLabel={planosSelectionLabel}
            executoresSetoresSelectionLabel={executoresSetoresSelectionLabel}
            ambientesEquipamentosSelectionLabel={ambientesEquipamentosSelectionLabel}
            maintenanceUnitsOptions={maintenanceUnitsOptions}
            maintenanceUnitsValues={maintenanceUnitsValues}
            onMaintenanceUnitsChange={handleMaintenanceUnitsChange}
            displayMode={displayMode}
            onDisplayModeChange={setDisplayMode}
            canViewOccurrences={canViewOccurrences}
            hideTypeTabs={true}
          />
        </MobileFiltersExpandable>
      )}
      <MobileDayListSection>
        {!isLoadingEvents && eventsForSelectedDay.length === 0 ? (
          <AgendaMobileDayEmptyState />
        ) : (
          <MobileDayCardList
            events={eventsForSelectedDay}
            getColorSet={getColorSet}
            isCompletedView={type === 'baixadas'}
            horasPrevistasLabel={horasPrevistasSelectedDay}
            onEventClick={onEventClick}
          />
        )}
      </MobileDayListSection>
    </div>
  );
};
